import { create } from 'zustand';
import { persist } from 'zustand/middleware';
import type { AuthUser } from '@cms/models';
import { authApi, getRoleDisplayName } from '../lib/api/auth.api';

const SESSION_UI_KEYS = [
  'ffshop-expanded-menus',
  'ffshop-sidebar-collapsed',
  'order-draft',
] as const;

function clearSessionUIState(): void {
  SESSION_UI_KEYS.forEach(key => {
    try {
      globalThis.localStorage?.removeItem(key);
    } catch {
      // ignore
    }
  });
}

type User = AuthUser;

/**
 * Kiểm tra scope của user có thỏa required scope không.
 * - Exact: 'user:read' thỏa 'user:read'.
 * - Pattern resource:*: 'user:*' thỏa mọi 'user:...' (user:read, user:write, ...).
 */
function scopeMatches(requiredScope: string, userScope: string): boolean {
  if (userScope === '*' || userScope === '*:*') return true;
  if (userScope.endsWith(':*')) {
    const prefix = userScope.slice(0, -2);
    return requiredScope === prefix || requiredScope.startsWith(`${prefix}:`);
  }
  return requiredScope === userScope;
}

/** JWT có thể không chứa `scopes`; role trong profile (sau loadProfile) vẫn có — merge để UI khớp backend. */
function getEffectiveUserScopes(get: () => Pick<AuthState, 'user' | 'getRolesArray'>): string[] {
  const tokenScopes = authApi.getScopesFromToken();
  const state = get();
  const roleScopes = state.getRolesArray(state.user ?? null).flatMap(r => r.scopes ?? []);
  return Array.from(new Set([...tokenScopes, ...roleScopes]));
}

interface AuthState {
  user: User | null;
  token: string | null;
  /** Tenant ID (from user or JWT); used by SaaS admin. */
  tenantId: string | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  /** Điền user từ JWT payload khi có token nhưng chưa có user (trước khi load profile). */
  setUserFromToken: () => void;
  login: (email: string, password: string) => Promise<void>;
  register: (email: string, password: string, name?: string) => Promise<void>;
  logout: () => Promise<void>;
  loadProfile: () => Promise<void>;
  requestPasswordReset: (email: string) => Promise<void>;
  resendVerificationEmail: (email: string) => Promise<void>;
  getRolesArray: (user: User | null) => Array<{ id: string; name: string; scopes: string[] }>;
  hasScope: (scope: string) => boolean;
  hasRole: (role: string) => boolean;
  hasAnyScope: (scopes: string[]) => boolean;
  /** Cập nhật token trong store (sau khi refresh). */
  setToken: (token: string | null) => void;
}

export const useAuthStore = create<AuthState>()(
  persist(
    (set, get) => ({
      user: null,
      token: null,
      tenantId: null,
      isAuthenticated: false,
      isLoading: false,

      setUserFromToken() {
        if (!authApi.getAccessToken()) return;
        const state = get();
        if (state.user != null) return;
        const userFromToken = authApi.getUserFromToken();
        if (userFromToken) {
          const tid = userFromToken.tenantId ?? authApi.getTenantIdFromToken();
          set({ user: userFromToken, tenantId: tid });
        }
      },

      async login(email: string, password: string) {
        set({ isLoading: true });
        try {
          const response = await authApi.login(email, password);
          const tid = response.user?.tenantId ?? authApi.getTenantIdFromToken();
          set({
            user: response.user,
            token: response.accessToken,
            tenantId: tid,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (error: unknown) {
          set({ isLoading: false });
          // Preserve error structure with code and messageKey from API response
          // Axios errors include response.data with error metadata (code, messageKey, message, details)
          // Throw error as-is to preserve full error structure for LoginPage
          throw error;
        }
      },

      async register(email: string, password: string, name?: string) {
        set({ isLoading: true });
        try {
          await authApi.register({ email, password, name });
          set({ isLoading: false });
        } catch (error: unknown) {
          set({ isLoading: false });
          // Preserve error structure with code and messageKey from API response
          // Throw error as-is to preserve full error structure
          throw error;
        }
      },

      async logout() {
        try {
          await authApi.logout();
        } catch {
          // ignore
        }
        clearSessionUIState();
        set({ user: null, token: null, tenantId: null, isAuthenticated: false });
      },

      async loadProfile() {
        if (!authApi.isAuthenticated()) {
          set({ user: null, isAuthenticated: false });
          return;
        }

        set({ isLoading: true });
        try {
          const profile = await authApi.getProfile();
          const tid = profile?.tenantId ?? authApi.getTenantIdFromToken();
          set({
            user: profile,
            token: authApi.getAccessToken(),
            tenantId: tid,
            isAuthenticated: true,
            isLoading: false,
          });
        } catch (err: unknown) {
          const status = (err as { response?: { status?: number } })?.response?.status;
          if (status === 401) {
            authApi.clearTokens();
            set({ user: null, token: null, tenantId: null, isAuthenticated: false, isLoading: false });
          } else {
            set(state => ({ ...state, isLoading: false }));
          }
        } finally {
          set(state => ({ ...state, isLoading: false }));
        }
      },

      async requestPasswordReset(email: string) {
        await authApi.requestPasswordReset(email);
      },

      async resendVerificationEmail(email: string) {
        await authApi.resendVerificationEmail(email);
      },

      /** User có một role; trả về mảng 1 phần tử để tương thích với code dùng getRolesArray(). */
      getRolesArray(user: User | null): Array<{ id: string; name: string; scopes: string[] }> {
        const role = user?.role;
        if (role == null) return [];
        const name = getRoleDisplayName(role);
        if (!name) return [];
        return [
          typeof role === 'string'
            ? { id: '', name: role, scopes: [] }
            : { id: role.id, name: role.name, scopes: role.scopes ?? [] },
        ];
      },

      hasScope(scope: string): boolean {
        const userScopes = getEffectiveUserScopes(get);
        return userScopes.some(us => scopeMatches(scope, us));
      },

      hasRole(roleName: string): boolean {
        const { user } = get();
        const roles = get().getRolesArray(user ?? null);
        return roles.some(role => role.name === roleName);
      },

      hasAnyScope(scopesToCheck: string[]): boolean {
        const userScopes = getEffectiveUserScopes(get);
        return scopesToCheck.some(scope => userScopes.some(us => scopeMatches(scope, us)));
      },

      setToken(token: string | null): void {
        set({ token });
      },
    }),
    {
      name: 'auth-storage',
      partialize: state => ({
        token: state.token,
        isAuthenticated: state.isAuthenticated,
      }),
    }
  )
);
